// ── THE PICTURE HEAT MAP (Clarity-style) ─────────────────────────────────────
//
// A real photograph of the sales page (frontend/public/heatmap/<view>/, taken
// from the tracking-free demo by frontend/scripts/capture-sales-heatmap.mjs)
// with a colour wash over it: hot where everyone still is, cooling as people
// leave. Everything drawn comes from GET /api/admin/sales-heatmap → picture
// (backend services/salesHeatmapPicture.js); this file only lays it out.
//
//   · hover (mouse) or touch-and-drag (phone) → a line: "62% reached this point · 26 of 42"
//   · marker lines: "Average fold", "Half have left"
//   · a tag at each buy button: "Saw 58% · clicked 4"
//   · the 5% table beside the picture (below it on a phone); a row click
//     scrolls the picture to that point
//
// Positions are FRACTIONS of the page, so the picture can be shown at any
// width: a point x% down the page is x% down the picture.

import { useState, useEffect, useRef, useMemo } from 'react';
import { C } from './adminShared';

// Clarity-like scale, 0 → 1 share of visitors: blue → green → yellow → orange → red.
const STOPS = [[0, [49, 110, 206]], [0.25, [46, 170, 110]], [0.5, [240, 205, 60]], [0.75, [240, 135, 45]], [1, [222, 45, 45]]];
const heat = (share, alpha = 0.42) => {
  const t = Math.max(0, Math.min(1, share));
  let i = 0;
  while (i < STOPS.length - 2 && t > STOPS[i + 1][0]) i++;
  const [a, ca] = STOPS[i], [b, cb] = STOPS[i + 1];
  const k = (t - a) / (b - a || 1);
  const c = ca.map((v, j) => Math.round(v + (cb[j] - v) * k));
  return `rgba(${c[0]},${c[1]},${c[2]},${alpha})`;
};
const fmtPct = (p) => (p == null ? '—' : `${Math.round(p)}%`);
const people = (n) => `${n} ${n === 1 ? 'person' : 'people'}`;

const VIEWS = [
  { id: 'mobile', label: 'Mobile', noun: 'phone visitor', picture: 'mobile' },
  { id: 'desktop', label: 'Desktop', noun: 'desktop visitor', picture: 'desktop' },
];

// The picture's own facts (tiles, size) — static files served with the admin.
const pictureCache = {};
const usePictureMeta = (view) => {
  const [meta, setMeta] = useState(pictureCache[view] || null);
  const [error, setError] = useState(null);
  useEffect(() => {
    if (pictureCache[view]) { setMeta(pictureCache[view]); return; }
    let on = true;
    setMeta(null); setError(null);
    fetch(`/heatmap/${view}/page.json`)
      .then(r => { if (!r.ok) throw new Error(`HTTP ${r.status}`); return r.json(); })
      .then(j => { pictureCache[view] = j; if (on) setMeta(j); })
      .catch(e => { if (on) setError(e.message || 'could not load the picture'); });
    return () => { on = false; };
  }, [view]);
  return { meta, error };
};

const useNarrow = () => {
  const q = '(max-width: 760px)';
  const [narrow, setNarrow] = useState(() => typeof window !== 'undefined' && window.matchMedia ? window.matchMedia(q).matches : false);
  useEffect(() => {
    if (!window.matchMedia) return;
    const m = window.matchMedia(q);
    const on = () => setNarrow(m.matches);
    m.addEventListener ? m.addEventListener('change', on) : m.addListener(on);
    return () => { m.removeEventListener ? m.removeEventListener('change', on) : m.removeListener(on); };
  }, []);
  return narrow;
};

const Legend = () => (
  <div style={{ display: 'flex', alignItems: 'center', gap: 6, fontSize: 10, color: C.accent }}>
    <span>Everyone</span>
    <span style={{ width: 120, height: 8, borderRadius: 4, background: `linear-gradient(to right, ${[1, 0.75, 0.5, 0.25, 0].map(s => heat(s, 0.9)).join(',')})` }} />
    <span>Few</span>
  </div>
);

// ── The picture with its overlay ────────────────────────────────────────────
const Picture = ({ meta, view: v, width, frameHeight, lineAt, setLineAt, frameRef }) => {
  const scale = width / meta.viewport.width;
  const H = Math.round(meta.page_height * scale);
  const curve = v.curve;
  const at = (x) => {
    const k = Math.max(0, Math.min(curve.length - 1, Math.round(x * 2)));
    return curve[k];
  };
  const wash = useMemo(() => {
    const stops = [];
    for (let x = 0; x <= 100; x += 1) {
      const c = curve[Math.round(x * 2)];
      stops.push(`${heat(v.base > 0 ? c.count / v.base : 0)} ${x}%`);
    }
    return `linear-gradient(to bottom, ${stops.join(', ')})`;
  }, [curve, v.base]);

  const pointFrom = (clientY, el) => {
    const r = el.getBoundingClientRect();
    const y = clientY - r.top;
    return Math.max(0, Math.min(100, (y / r.height) * 100));
  };
  const onMove = (e) => setLineAt(pointFrom(e.clientY, e.currentTarget));
  const onTouch = (e) => { const t = e.touches && e.touches[0]; if (t) setLineAt(pointFrom(t.clientY, e.currentTarget)); };
  const line = lineAt != null ? at(lineAt) : null;

  const Marker = ({ x, label, color }) => (x == null ? null : (
    <div style={{ position: 'absolute', left: 0, right: 0, top: `${x}%`, borderTop: `2px dashed ${color}`, pointerEvents: 'none', zIndex: 3 }}>
      <span style={{ position: 'absolute', left: 4, top: 2, fontSize: 10, fontWeight: 700, color: '#fff', background: color, borderRadius: 4, padding: '1px 6px', whiteSpace: 'nowrap' }}>{label}</span>
    </div>
  ));

  return (
    <div ref={frameRef} data-testid="heatmap-frame" style={{
      width: width + 2, height: frameHeight, overflowY: 'auto', overflowX: 'hidden', border: `1px solid ${C.border}`,
      borderRadius: 10, background: '#fff', position: 'relative', WebkitOverflowScrolling: 'touch', flexShrink: 0,
    }}>
      <div
        style={{ position: 'relative', width, height: H }}
        onMouseMove={onMove}
        onMouseLeave={() => setLineAt(null)}
        onTouchStart={onTouch}
        onTouchMove={onTouch}
      >
        {meta.tiles.map(t => (
          <img key={t.file} src={`/heatmap/${meta.view}/${t.file}`} alt={t.top === 0 ? 'The sales page' : ''} loading="lazy" draggable={false}
            style={{ position: 'absolute', left: 0, top: Math.round(t.top * scale), width, height: Math.round(t.height * scale) + 1, display: 'block', userSelect: 'none' }} />
        ))}
        <div data-testid="heatmap-wash" style={{ position: 'absolute', inset: 0, background: wash, pointerEvents: 'none', zIndex: 2 }} />
        <Marker x={v.fold ? v.fold.pct : null} label={`Average fold · ${fmtPct(v.fold && v.fold.pct)} of page`} color="#6B3E0A" />
        <Marker x={v.halfGone && v.halfGone.x != null ? v.halfGone.x : null} label="Half have left" color="#B83418" />
        {v.buttons.map(b => (b.topPct == null ? null : (
          <div key={b.id} data-testid="heatmap-button" style={{ position: 'absolute', right: 4, top: `${b.topPct}%`, transform: 'translateY(-100%)', zIndex: 4, pointerEvents: 'none' }}>
            <span style={{ display: 'inline-block', fontSize: 10, lineHeight: 1.3, fontWeight: 700, color: '#fff', background: 'rgba(42,20,0,0.82)', borderRadius: 5, padding: '2px 6px', textAlign: 'right' }}>
              {b.id} · Saw {fmtPct(b.sawPct)} · clicked {b.clicked}
              {b.isFoldButton && b.sawWithoutScrollingPct != null ? <><br />saw without scrolling {fmtPct(b.sawWithoutScrollingPct)}</> : null}
            </span>
          </div>
        )))}
        {line && (
          <div data-testid="heatmap-line" style={{ position: 'absolute', left: 0, right: 0, top: `${lineAt}%`, borderTop: '2px solid #2A1400', pointerEvents: 'none', zIndex: 5 }}>
            <span style={{ position: 'absolute', left: '50%', transform: 'translate(-50%, -120%)', fontSize: 11, fontWeight: 700, color: '#fff', background: '#2A1400', borderRadius: 6, padding: '3px 8px', whiteSpace: 'nowrap' }}>
              {fmtPct(line.pct)} reached this point · {line.count} of {v.base}
            </span>
          </div>
        )}
      </div>
    </div>
  );
};

// ── The 5% table ────────────────────────────────────────────────────────────
const StepTable = ({ table, onPick }) => (
  <table data-testid="heatmap-table" style={{ width: '100%', borderCollapse: 'collapse', fontSize: 11 }}>
    <thead>
      <tr style={{ borderBottom: `2px solid ${C.border}`, textAlign: 'left', color: C.heading }}>
        <th style={{ padding: '4px 6px' }}>% of page</th>
        <th style={{ padding: '4px 6px' }}>Reached</th>
        <th style={{ padding: '4px 6px' }}>Drop-off</th>
      </tr>
    </thead>
    <tbody>
      {table.map(t => (
        <tr key={t.x} onClick={() => onPick(t.x)} style={{ borderBottom: `1px solid ${C.border}`, cursor: 'pointer' }} title="Show this point on the picture">
          <td style={{ padding: '4px 6px', fontWeight: 600 }}>
            <span style={{ display: 'inline-block', width: 8, height: 8, borderRadius: 2, marginRight: 6, background: heat(t.pct == null ? 0 : t.pct / 100, 0.9) }} />
            {t.x}%
          </td>
          <td style={{ padding: '4px 6px' }}><strong>{fmtPct(t.pct)}</strong> <span style={{ color: C.accent }}>· {t.count}</span></td>
          <td style={{ padding: '4px 6px', color: C.accent }}>{t.droppedPct == null ? '—' : `${Math.round(t.droppedPct)}%`}</td>
        </tr>
      ))}
    </tbody>
  </table>
);

const fmtWhen = (iso) => {
  const d = new Date(iso);
  return Number.isNaN(d.getTime()) ? '' : d.toLocaleString('en-GB', { day: 'numeric', month: 'short', hour: '2-digit', minute: '2-digit' });
};

// ── PUBLIC ──────────────────────────────────────────────────────────────────
export const PictureHeatmap = ({ picture, paidOnly, view, setView }) => {
  const narrow = useNarrow();
  const opt = VIEWS.find(x => x.id === view);
  const { meta, error } = usePictureMeta(opt.picture);
  const v = picture.views[view];
  const [lineAt, setLineAt] = useState(null);
  const frameRef = useRef(null);
  const boxRef = useRef(null);
  const [boxW, setBoxW] = useState(0);
  useEffect(() => {
    const el = boxRef.current; if (!el) return;
    const on = () => setBoxW(el.clientWidth);
    on();
    if (typeof ResizeObserver === 'undefined') return;
    const ro = new ResizeObserver(on); ro.observe(el);
    return () => ro.disconnect();
  }, []);
  useEffect(() => { setLineAt(null); if (frameRef.current) frameRef.current.scrollTop = 0; }, [view]);

  const tableW = narrow ? 0 : 250;
  const room = Math.max(200, (boxW || 360) - tableW - (narrow ? 0 : 20) - 2);
  const width = meta ? Math.floor(Math.min(meta.viewport.width, room)) : 0;
  const frameHeight = narrow ? Math.round(Math.min(620, (typeof window !== 'undefined' ? window.innerHeight : 800) * 0.7)) : 700;

  const pick = (x) => {
    setLineAt(x);
    const f = frameRef.current; if (!f || !meta) return;
    const H = meta.page_height * (width / meta.viewport.width);
    f.scrollTo({ top: Math.max(0, (x / 100) * H - frameHeight / 3), behavior: 'smooth' });
  };

  return (
    <div ref={boxRef} data-testid="picture-heatmap" style={{ marginBottom: 18 }}>
      <div role="group" aria-label="Device" style={{ display: 'inline-flex', border: `1px solid ${C.heading}`, borderRadius: 999, overflow: 'hidden', marginBottom: 10 }}>
        {VIEWS.map(x => (
          <button key={x.id} type="button" onClick={() => setView(x.id)} aria-pressed={view === x.id} style={{
            fontSize: 12, fontWeight: view === x.id ? 800 : 600, padding: '6px 16px', cursor: 'pointer', border: 'none',
            background: view === x.id ? C.heading : 'transparent', color: view === x.id ? '#fff' : C.heading,
          }}>{x.label}</button>
        ))}
      </div>

      <div data-testid="heatmap-baseline" style={{ fontSize: 12, color: C.heading, marginBottom: 4 }}>
        Based on <strong>{v.base} {opt.noun}{v.base === 1 ? '' : 's'}</strong>
        {picture.scrollMaxSince ? <> since {fmtWhen(picture.scrollMaxSince)}</> : null}
        {' · '}{paidOnly ? 'paid only' : 'all traffic'}.
      </div>
      <div style={{ fontSize: 10.5, color: C.accent, marginBottom: 8, lineHeight: 1.5 }}>
        A visitor counts as reaching a point when it was on their screen (the bottom of their screen at their furthest scroll).
        {v.usedMedian > 0 ? <> {people(v.usedMedian)} left no scroll record, so {v.usedMedian === 1 ? 'is' : 'are'} counted as seeing a typical first screen.</> : null}
        {picture.unknownExcluded > 0 ? <> {people(picture.unknownExcluded)} whose device couldn’t be told {picture.unknownExcluded === 1 ? 'is' : 'are'} in neither view.</> : null}
      </div>
      {picture.stale === true && (
        <div data-testid="heatmap-stale" style={{ fontSize: 11.5, color: C.text, background: C.amberBg, border: `1px solid ${C.cta}`, borderRadius: 8, padding: '8px 10px', marginBottom: 10 }}>
          ⚠ Your sales page changed since this picture was taken ({fmtWhen(picture.capturedAt)}) — retake it
          (<code>node frontend/scripts/capture-sales-heatmap.mjs</code>, then commit).
        </div>
      )}

      {v.base === 0 ? (
        <div style={{ fontSize: 12, color: C.accent, padding: '6px 0 10px' }}>No {opt.noun}s in this range yet — nothing to draw.</div>
      ) : error ? (
        <div style={{ fontSize: 12, color: C.red }}>✕ Couldn’t load the picture: {error}</div>
      ) : !meta ? (
        <div style={{ fontSize: 12, color: C.accent }}>Loading the picture…</div>
      ) : (
        <div style={{ display: 'flex', flexDirection: narrow ? 'column' : 'row', gap: 20, alignItems: 'flex-start' }}>
          <div>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: 8, marginBottom: 6, width: width + 2 }}>
              <Legend />
              <span style={{ fontSize: 10, color: C.accent }}>{narrow ? 'Touch and drag' : 'Hover'} to read a point</span>
            </div>
            <Picture meta={meta} view={v} width={width} frameHeight={frameHeight} lineAt={lineAt} setLineAt={setLineAt} frameRef={frameRef} />
          </div>
          <div style={{ width: narrow ? '100%' : tableW, flexShrink: 0 }}>
            <div style={{ fontSize: 10, fontWeight: 700, color: C.heading, textTransform: 'uppercase', letterSpacing: '0.06em', marginBottom: 6 }}>
              Every 5% of the page — of {people(v.base)}
            </div>
            <StepTable table={v.table} onPick={pick} />
            <div style={{ fontSize: 10, color: C.accent, marginTop: 8, lineHeight: 1.5 }}>
              Average fold {fmtPct(v.fold && v.fold.pct)} of the page
              {v.halfGone && v.halfGone.neverBelowHalf ? ' · more than half reach the bottom' : v.halfGone && v.halfGone.x != null ? ` · half have left by ${Math.round(v.halfGone.x)}%` : ''}.
              {' '}Picture taken {fmtWhen(picture.capturedAt)}.
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export const PICTURE_TO_CARD_VIEW = { mobile: 'mobile', desktop: 'other' };
